import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { dirOf, isLocale } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * ברירת המחדל של Radix היא ltr, והיא נדבקת כ-`dir="ltr"` על השורש ומהפכת את
 * כל מה שבתוך הלשוניות. ה-DirectionProvider ב-main.tsx מכסה את כל ה-primitives;
 * ברירת המחדל כאן (לפי השפה) היא רשת ביטחון לכל שימוש מחוץ לעץ הזה (בדיקות, Storybook).
 */
export function Tabs({ dir, ...props }: ComponentProps<typeof TabsPrimitive.Root>) {
  // לפי שפת הממשק — rtl בעברית, ltr באנגלית.
  const { i18n } = useTranslation();
  return <TabsPrimitive.Root dir={dir ?? dirOf(isLocale(i18n.language) ? i18n.language : 'he')} {...props} />;
}

export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn('flex gap-1 overflow-x-auto border-b border-border [scrollbar-width:none]', className)}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        '-mb-px flex min-h-11 shrink-0 items-center whitespace-nowrap border-b-2 border-transparent px-3 text-xs font-medium text-fg-muted',
        'transition-colors duration-(--duration-fast) hover:text-fg',
        'data-[state=active]:border-accent data-[state=active]:text-accent',
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn('pt-4', className)} {...props} />;
}
